import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import StoryCard from '../components/StoryCard';
import ContinueReading from '../components/ContinueReading';

interface DiscoverPageProps {
  onNavigate: (page: string, data?: any) => void;
  initialGenre?: string;
  initialTag?: string;
  user?: any;
}

const GENRES = ['all', 'Action', 'Adventure', 'Comedy', 'Crime', 'Dark Fantasy', 'Drama', 'Dystopian', 'Erotica', 'Fanfiction', 'Fantasy', 'Historical', 'Horror', 'LGBTQ+', 'LitRPG', 'Mystery', 'New Adult', 'Paranormal', 'Poetry', 'Progression Fantasy', 'Romance', 'Romantasy', 'Sci-Fi', 'Short Story', 'Slice of Life', 'Thriller', 'Urban Fantasy', 'Werewolf', 'Western', 'Young Adult'];
const SORTS = [
  { value: 'new', label: 'Newest' },
  { value: 'trending', label: 'Trending' },
  { value: 'top-rated', label: 'Top Rated' },
  { value: 'most-followed', label: 'Most Followed' },
  { value: 'updated', label: 'Recently Updated' },
];

export default function DiscoverPage({ onNavigate, initialGenre, initialTag, user }: DiscoverPageProps) {
  const [stories, setStories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [genre, setGenre] = useState(initialGenre || 'all');
  const [sort, setSort] = useState('new');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [popularTags, setPopularTags] = useState<{ tag: string; count: number }[]>([]);
  const [activeTags, setActiveTags] = useState<string[]>(initialTag ? [initialTag] : []);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [matureFilter, setMatureFilter] = useState<'' | 'true' | 'false'>('');
  const [minWords, setMinWords] = useState('');
  const [maxWords, setMaxWords] = useState('');

  useEffect(() => {
    api.discover.tags()
      .then((tags: { tag: string; count: number }[]) => setPopularTags(tags.slice(0, 30)))
      .catch(console.error);
  }, []);

  useEffect(() => {
    setLoading(true);
    const params: Record<string, string> = { page: String(page) };
    if (genre !== 'all') params.genre = genre;
    if (sort) params.sort = sort;
    if (search) params.q = search;
    if (status) params.status = status;
    if (activeTags.length > 0) params.tags = activeTags.join(',');
    if (matureFilter !== '') params.mature = matureFilter;
    if (minWords) params.minWords = minWords;
    if (maxWords) params.maxWords = maxWords;

    api.discover.search(params)
      .then(data => {
        setStories(data.stories);
        setTotalPages(data.totalPages);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [genre, sort, search, status, page, activeTags, matureFilter, minWords, maxWords]);

  const toggleTag = (tag: string) => {
    setActiveTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
    setPage(1);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <ContinueReading onNavigate={onNavigate} user={user} />
      <h1 className="text-3xl font-bold mb-6">Discover Stories</h1>

      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <input
          type="text"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search by title, synopsis, or author..."
          className="flex-1 bg-gray-900 border border-gray-800 rounded-xl px-4 py-2.5 text-sm focus:border-ink-500 focus:outline-none"
        />
        <select
          value={sort}
          onChange={e => { setSort(e.target.value); setPage(1); }}
          className="bg-gray-900 border border-gray-800 rounded-xl px-4 py-2.5 text-sm focus:outline-none"
        >
          {SORTS.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <select
          value={status}
          onChange={e => { setStatus(e.target.value); setPage(1); }}
          className="bg-gray-900 border border-gray-800 rounded-xl px-4 py-2.5 text-sm focus:outline-none"
        >
          <option value="">All Status</option>
          <option value="ONGOING">Ongoing</option>
          <option value="COMPLETED">Completed</option>
          <option value="HIATUS">Hiatus</option>
        </select>
      </div>

      {/* Advanced Filters Toggle */}
      <div className="mb-4">
        <button
          onClick={() => setShowAdvanced(v => !v)}
          className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          Advanced Filters
          {(matureFilter !== '' || minWords || maxWords) && (
            <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-ink-600 text-white rounded-full">
              {[matureFilter !== '', !!minWords, !!maxWords].filter(Boolean).length}
            </span>
          )}
        </button>
        {showAdvanced && (
          <div className="mt-3 p-4 bg-gray-900 border border-gray-800 rounded-xl grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Mature Content Filter */}
            <div>
              <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">Mature Content</label>
              <div className="flex gap-2">
                {([['', 'All'], ['false', 'Clean'], ['true', '18+ Only']] as const).map(([val, label]) => (
                  <button
                    key={val}
                    onClick={() => { setMatureFilter(val); setPage(1); }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      matureFilter === val ? 'bg-ink-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {/* Word Count Range */}
            <div>
              <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">Min Total Words</label>
              <select
                value={minWords}
                onChange={e => { setMinWords(e.target.value); setPage(1); }}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-ink-500"
              >
                <option value="">Any</option>
                <option value="1000">1,000+</option>
                <option value="5000">5,000+</option>
                <option value="10000">10,000+</option>
                <option value="50000">50,000+</option>
                <option value="100000">100,000+</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">Max Total Words</label>
              <select
                value={maxWords}
                onChange={e => { setMaxWords(e.target.value); setPage(1); }}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-ink-500"
              >
                <option value="">Any</option>
                <option value="5000">Up to 5,000</option>
                <option value="10000">Up to 10,000</option>
                <option value="50000">Up to 50,000</option>
                <option value="100000">Up to 100,000</option>
                <option value="200000">Up to 200,000</option>
              </select>
            </div>
            {/* Reset Advanced */}
            {(matureFilter !== '' || minWords || maxWords) && (
              <div className="sm:col-span-3 flex justify-end">
                <button
                  onClick={() => { setMatureFilter(''); setMinWords(''); setMaxWords(''); setPage(1); }}
                  className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
                >
                  Reset advanced filters
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex gap-2 flex-wrap mb-4">
        {GENRES.map(g => (
          <button
            key={g}
            onClick={() => { setGenre(g); setPage(1); }}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              genre === g
                ? 'bg-ink-600 text-white'
                : 'bg-gray-900 text-gray-400 border border-gray-800 hover:border-ink-500/50'
            }`}
          >
            {g === 'all' ? 'All Genres' : g}
          </button>
        ))}
      </div>

      {popularTags.length > 0 && (
        <div className="mb-6">
          <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider font-medium">Popular Tags</p>
          <div className="flex gap-2 flex-wrap">
            {popularTags.map(({ tag, count }) => (
              <button
                key={tag}
                onClick={() => toggleTag(tag)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  activeTags.includes(tag)
                    ? 'bg-ink-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-300'
                }`}
              >
                {tag} <span className="text-gray-500 ml-0.5">({count})</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {activeTags.length > 0 && (
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs text-gray-500">Filtering by:</span>
          {activeTags.map(tag => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-ink-600/20 text-ink-400 rounded-full text-xs"
            >
              {tag}
              <button onClick={() => toggleTag(tag)} className="hover:text-white">×</button>
            </span>
          ))}
          <button
            onClick={() => { setActiveTags([]); setPage(1); }}
            className="text-xs text-gray-500 hover:text-gray-300 ml-1"
          >
            Clear all
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-3 border-ink-400 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : stories.length > 0 ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {stories.map(story => (
              <StoryCard
                key={story.id}
                story={story}
                onClick={() => onNavigate('story', { id: story.id, slug: story.slug })}
                onTagClick={(tag) => toggleTag(tag)}
              />
            ))}
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 bg-gray-800 rounded-lg disabled:opacity-30"
              >
                Previous
              </button>
              <span className="text-sm text-gray-400">Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 bg-gray-800 rounded-lg disabled:opacity-30"
              >
                Next
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-20">
          <span className="text-4xl block mb-4">📚</span>
          <h2 className="text-xl font-bold mb-2">No stories found</h2>
          <p className="text-gray-400">Try adjusting your filters or be the first to write in this genre!</p>
        </div>
      )}
    </div>
  );
}
